import clsx from 'clsx';
import React from 'react';

import { SecondaryButton } from '@components/Button/variants';
import NameList from '@components/NameList';
import useEventTracking from '@hooks/useEventTracking';
import { Theme } from '@interfaces/global';
import { article_path } from '@routes';
import { ClickTarget, ContentType } from '@tracking';

import { ScoopedArticleProps } from './interfaces';

const ScoopedArticle = ({
  article,
  visibleArticles,
  hasMoreArticles,
  index,
  onLoadMore,
}: ScoopedArticleProps) => {
  const { Track, trackEvent } = useEventTracking({
    contentType: ContentType.Article,
    contentId: article.id,
    creatorIds: article.authors.map((author) => author.id),
    contentIndex: index,
  });

  const handleExternalLinkClick = (pickupId: number) => {
    trackEvent({ clickTarget: ClickTarget.ScoopsExternalLink, contentId: pickupId });
  };

  return (
    <Track>
      <article
        key={article.id}
        className={clsx(
          'mb-40 border-l-0 border-r-0 border-t-0 border-solid border-grayscale-gray pb-40',
          hasMoreArticles && index === visibleArticles.length - 1 ? 'border-b-0' : 'border-b'
        )}
      >
        <span className="mb-0 block font-suisse-intl-mono text-caption-sm text-primary-magenta">
          {article.highlight}
        </span>
        <h2 className="mb-0 mt-4 text-pretty font-suisse-works text-headline-xs">
          <a
            href={article_path(article.slug)}
            className="transition-colors hover:text-primary-magenta dark:hover:text-primary-pink"
          >
            {article.title}
          </a>
        </h2>

        <div className="mb-16 !text-byline-sm">
          {article.authors && article.authors.length > 0 && (
            <span className="mr-12">
              <NameList authors={article.authors} theme={Theme.Light} />
            </span>
          )}
          {article.publishedAt && (
            <time className="text-overline-sm text-grayscale-gray">{article.publishedAt}</time>
          )}
        </div>

        <div>
          <h3 className="m-0 font-suisse-intl-mono text-overline-sm text-grayscale-gray">
            Followed by
          </h3>
          <ul className="m-0 list-none p-0">
            {article.pickups.map((pickup) => (
              <li key={pickup.id}>
                <a
                  href={pickup.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => handleExternalLinkClick(pickup.id)}
                  className="m-0 block p-0 font-suisse-intl hover:underline"
                >
                  {pickup.publication && (
                    <span className="text-caption-lg">{pickup.publication}:</span>
                  )}
                  {pickup.title && (
                    <span className="text-caption-lg text-grayscale-gray"> {pickup.title}</span>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </div>
        {hasMoreArticles && index === visibleArticles.length - 1 && (
          <SecondaryButton
            as="button"
            size="small"
            onClick={() => onLoadMore()}
            className="my-32 w-full"
          >
            Read More
          </SecondaryButton>
        )}
      </article>
    </Track>
  );
};

export default ScoopedArticle;
